import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { DURATION_UNITS, type DurationUnit } from '@/utils/durationFormatting';

interface DurationInputProps {
  amount: number;
  unit: DurationUnit;
  onChange: (amount: number, unit: DurationUnit) => void;
  isDisabled?: boolean;
  hasError?: boolean;
  ariaLabel?: string;
  className?: string;
}

const UNIT_LABEL_KEYS: Record<DurationUnit, { short: string; full: string }> = {
  s: { short: 'duration.unitSecondsShort', full: 'duration.unitSecondsFull' },
  min: { short: 'duration.unitMinutesShort', full: 'duration.unitMinutesFull' },
  h: { short: 'duration.unitHoursShort', full: 'duration.unitHoursFull' },
};

export function DurationInput({
  amount,
  unit,
  onChange,
  isDisabled = false,
  hasError = false,
  ariaLabel,
  className,
}: DurationInputProps) {
  const { t } = useTranslation('common');

  return (
    <div className={cn('flex gap-2', className)}>
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={Number.isNaN(amount) ? '' : amount}
        disabled={isDisabled}
        hasError={hasError}
        aria-label={ariaLabel === undefined ? t('duration.amountAriaLabel') : ariaLabel}
        onChange={function updateAmount(changeEvent) {
          const rawAmount = changeEvent.target.value.trim();
          onChange(rawAmount.length === 0 ? Number.NaN : Number(rawAmount), unit);
        }}
        className="w-24"
      />
      <div
        role="group"
        aria-label={t('duration.unitGroupAriaLabel')}
        className="inline-flex rounded-xl border border-zooborder overflow-hidden shrink-0"
      >
        {DURATION_UNITS.map(function renderUnitOption(unitOption, index) {
          const isSelected = unitOption === unit;
          return (
            <button
              key={unitOption}
              type="button"
              disabled={isDisabled}
              aria-pressed={isSelected}
              aria-label={t(UNIT_LABEL_KEYS[unitOption].full)}
              onClick={function selectThisUnit() {
                onChange(amount, unitOption);
              }}
              className={cn(
                'min-w-11 h-11 px-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zooprimary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
                isSelected
                  ? 'bg-zooprimary text-zoowhite'
                  : 'bg-zoooffwhite text-zoomuted hover:text-zooindigo',
                index > 0 ? 'border-l border-zooborder' : ''
              )}
            >
              {t(UNIT_LABEL_KEYS[unitOption].short)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
